// src/views/pages/restaurant-reservations/waitlist/components/SidebarPanel.jsx
import React from "react";
import PropTypes from "prop-types";
import { Card, Divider, List, Space, Typography } from "antd";
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts"; // Added Recharts for mini charts
import moment from "moment";
import StatCard from "./StatCard";
import { Users, Clock, MapPin, AlertTriangle } from "react-feather";

const { Title, Text } = Typography;

function SidebarPanel({ waitStats, occupancy, isWeekend, notifyLog, auditLog }) {
  // Mock chart data for wait trends (enhance with real data)
  const waitTrendData = [
    { time: "10AM", wait: 10 },
    { time: "11AM", wait: 15 },
    { time: "12PM", wait: 25 },
    { time: "1PM", wait: 20 },
  ];

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <StatCard
        title="Active Queue"
        value={`${waitStats.activeCount}`}
        icon={<Users size={18} />}
        hint={`${waitStats.waiting} waiting • ${waitStats.notified} notified`}
      />
      <StatCard
        title="Avg Wait"
        value={`${waitStats.avgWait} min`}
        icon={<Clock size={18} />}
        hint={isWeekend ? "Weekend peak active" : "Weekday normal"}
      />
      <StatCard
        title="Tables"
        value={`${occupancy.occupied}/${occupancy.total} occupied`}
        icon={<MapPin size={18} />}
        hint={`${occupancy.available} available • ${occupancy.pct}% occupancy`}
      />
      <StatCard
        title="Overruns"
        value={`${waitStats.overruns}`}
        icon={<AlertTriangle size={18} />}
        hint="Guests over ETA – Monitor closely"
      />

      <Card style={{ borderRadius: 16, border: "1px solid #eef2f7" }}>
        <Title level={5} style={{ margin: 0 }}>
          Wait Time Trend
        </Title>
        <Divider style={{ margin: "10px 0" }} />
        <ResponsiveContainer width="100%" height={120}>
          <LineChart data={waitTrendData}>
            <XAxis dataKey="time" />
            <YAxis />
            <RechartsTooltip />
            <Line type="monotone" dataKey="wait" stroke="#8884d8" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <Card style={{ borderRadius: 16, border: "1px solid #eef2f7" }}>
        <Title level={5} style={{ margin: 0 }}>
          Recent Notifications
        </Title>
        <Divider style={{ margin: "10px 0" }} />
        <List
          size="small"
          dataSource={notifyLog.slice(0, 6)}
          locale={{ emptyText: <Text type="secondary">No notifications yet</Text> }}
          renderItem={(n) => (
            <List.Item style={{ padding: "8px 0" }}>
              <Text type="secondary">
                {moment(n.at).format("h:mm A")} • {n.msg} → {n.to}
              </Text>
            </List.Item>
          )}
        />
      </Card>

      <Card style={{ borderRadius: 16, border: "1px solid #eef2f7" }}>
        <Title level={5} style={{ margin: 0 }}>
          Audit Trail
        </Title>
        <Divider style={{ margin: "10px 0" }} />
        <List
          size="small"
          dataSource={auditLog.slice(-8).reverse()}
          renderItem={(a) => (
            <List.Item style={{ padding: "8px 0" }}>
              <Text type="secondary">
                {moment(a.at).format("h:mm A")} • <b>{a.who}:</b> {a.action}
              </Text>
            </List.Item>
          )}
        />
      </Card>
    </Space>
  );
}

SidebarPanel.propTypes = {
  waitStats: PropTypes.object.isRequired,
  occupancy: PropTypes.object.isRequired,
  isWeekend: PropTypes.bool.isRequired,
  notifyLog: PropTypes.array.isRequired,
  auditLog: PropTypes.array.isRequired,
};

export default SidebarPanel;